// packages/core/src/badges/BadgeTypes.ts
// ─────────────────────────────────────────────────────────────────────────
// BADGE SYSTEM — type vocabulary (pure, no I/O, no React, no storage).
//
// Badges are PERMANENT PROOF of meaningful accomplishment. They are NOT the
// gem economy and NOT XP:
//   • XP    → continuous learning progression (xpSlice)
//   • Gems  → spendable celebration currency (economySlice)
//   • Badges→ permanent, un-spendable proof-of-accomplishment (this system)
//
// Locked product rules encoded by the type system:
//   1. Every badge represents a MEANINGFUL action (never "opened the app").
//   2. Badges never unlock content, never pay Gems/XP. No `reward` field.
//   3. Badge IDs are PERMANENT once shipped — treat like lesson IDs. Display
//      name / description / icon may change; id + meaning may not.
//   4. Locked badges are always visible (no hidden/secret badges in V1).
//
// The registry (badgesRegistry.ts) is the single source of truth for which
// badges exist. Evaluation (badgeEvaluation.ts) is pure: it maps a snapshot
// of provable facts → the set of earned badge ids.
// ─────────────────────────────────────────────────────────────────────────

/** The six V1 categories, in product-story order:
 *  start → test on your numbers → prove mastery → show up → explore
 *  worldviews → move down the freedom path. Order is meaningful and is the
 *  order categories render on the trophy case. */
export const BADGE_CATEGORIES = [
	'getting-started',
	'run-the-numbers',
	'mastery',
	'consistency',
	'worldviews',
	'freedom-path',
] as const;

export type BadgeCategory = (typeof BADGE_CATEGORIES)[number];

export interface BadgeCategoryMeta {
	id: BadgeCategory;
	name: string;
	/** One-line description shown on the category card. */
	description: string;
	/** Display order on the trophy case (lower = first). */
	order: number;
}

export const BADGE_CATEGORY_META: Record<BadgeCategory, BadgeCategoryMeta> = {
	'getting-started': {
		id: 'getting-started',
		name: 'Getting Started',
		description: 'Your first proof that you started training.',
		order: 1,
	},
	'run-the-numbers': {
		id: 'run-the-numbers',
		name: 'Run the Numbers',
		description:
			'Test ideas against your actual life, not someone else’s spreadsheet.',
		order: 2,
	},
	mastery: {
		id: 'mastery',
		name: 'Mastery',
		description: 'Proof that the material is sticking.',
		order: 3,
	},
	consistency: {
		id: 'consistency',
		name: 'Consistency',
		description: 'Proof that you keep showing up.',
		order: 4,
	},
	worldviews: {
		id: 'worldviews',
		name: 'Worldviews',
		description:
			'Proof that you are learning how different money philosophies think.',
		order: 5,
	},
	'freedom-path': {
		id: 'freedom-path',
		name: 'Freedom Path',
		description:
			'Proof that you are moving through the full training path.',
		order: 6,
	},
};

/** Suggested icon family per category (the UI maps these to SVG glyphs). */
export type BadgeIconKind =
	| 'spark'
	| 'check'
	| 'door'
	| 'step'
	| 'calculator'
	| 'target'
	| 'compass'
	| 'graph'
	| 'brain'
	| 'lock'
	| 'book'
	| 'medal'
	| 'flame'
	| 'calendar'
	| 'chain'
	| 'prism'
	| 'globe'
	| 'conversation'
	| 'mountain'
	| 'flag'
	| 'path';

/**
 * Release gate. V1 badges ship now; V1.1 badges are DEFINED (so the engine,
 * the page, and retroactive logic all know about them) but are NOT displayed
 * or awarded until they are promoted to 'v1'. This keeps a single registry
 * while honoring the "hold for content maturity" product decision.
 */
export type BadgeRelease = 'v1' | 'v1.1';

/**
 * How a badge can be earned with respect to historical data:
 *   • 'retroactive' — the app can honestly prove the user already earned it
 *     from durable stored facts (completed lessons, passed quizzes, mastered
 *     modules, streak numbers, level, stored FI number, stored scenarios).
 *     These are re-checked on every boot sweep.
 *   • 'forward-only' — depends on a NEW behavior the app cannot prove from
 *     history (a fresh simulator run, an Impact Lab run, debate engagement).
 *     Only awarded when the live event fires; never granted by the boot sweep.
 */
export type BadgeAwardMode = 'retroactive' | 'forward-only';

/** A static badge definition. Pure data — no earned state lives here. */
export interface BadgeDef {
	/** PERMANENT id. Never change once shipped. */
	id: string;
	category: BadgeCategory;
	name: string;
	/** One-line, adult, past-tense accomplishment copy. */
	description: string;
	/** Plain-language unlock condition shown on locked cards. */
	unlockCondition: string;
	icon: BadgeIconKind;
	release: BadgeRelease;
	awardMode: BadgeAwardMode;
	/** Optional "Next" nudge shown on the earned card detail. */
	nextAction?: string;
	/**
	 * Optional progress target for locked-card progress display
	 * (e.g. "1 / 3 modules"). When set, evaluation may report `current`.
	 * Purely cosmetic — the unlock predicate is still the source of truth.
	 */
	progressTarget?: number;
	/** Short noun for the progress unit, e.g. 'modules', 'schools', 'days'. */
	progressUnit?: string;
	/**
	 * Celebration weight. 'small' → toast; 'result' → result-screen callout
	 * (set by the surface, not here); 'major' → bigger moment. Defaults small.
	 */
	celebration?: 'small' | 'major';
}

/** Per-user earned record. Stored web-side; shape is API-ready. */
export interface EarnedBadge {
	badgeId: string;
	/** ISO date the badge was first earned. */
	earnedAt: string;
	/** True if granted by a retroactive boot sweep (vs a live event). */
	retroactive: boolean;
}

/** A badge joined with its earned state for rendering. */
export interface BadgeView extends BadgeDef {
	earned: boolean;
	earnedAt: string | null;
	/** Progress toward unlock, when the badge declares a progressTarget. */
	progressCurrent?: number;
}
